import { useState, useEffect } from 'preact/hooks';
import { activeContactSignal, activeSummaryJobSignal } from '../../state/contacts.store';
import { isAiChatDrawerOpenSignal } from '../../state/ui.store';
import { ArrowLeft, Sparkles, Bot, Users, Loader2 } from 'lucide-preact';

interface HeaderProps {
  onOpenSummaryRangeModal: () => void;
}

export function Header({ onOpenSummaryRangeModal }: HeaderProps) {
  const contact = activeContactSignal.value;
  const summaryJob = activeSummaryJobSignal.value;
  const [imgError, setImgError] = useState<boolean>(false);

  useEffect(() => {
    setImgError(false);
  }, [contact?.phone_number, contact?.profile_pic_url]);

  if (!contact) return null;

  const isGroup = Boolean(contact.is_group || (contact.jid && contact.jid.includes('@g.us')));
  const initial = contact.name ? contact.name.charAt(0).toUpperCase() : '?';
  const showPic = Boolean(contact.profile_pic_url && !imgError);

  const isJobActive = Boolean(summaryJob && summaryJob.status !== 'COMPLETED' && summaryJob.status !== 'FAILED');
  const pct = Math.min(Math.max(summaryJob?.percent || 0, 5), 100);

  let statusLabel = 'Processando resumo...';
  if (summaryJob?.status === 'TRANSCRIBING') {
    statusLabel = (summaryJob.totalMedia && summaryJob.totalMedia > 0)
      ? `Transcrevendo (${summaryJob.processedMedia || 0}/${summaryJob.totalMedia})`
      : 'Transcrevendo mídias...';
  } else if (summaryJob?.status === 'EXTRACTING_ACTIONS' || summaryJob?.stage === 'EXTRACTING_ACTIONS') {
    statusLabel = 'Extraindo tarefas...';
  } else if (summaryJob?.status === 'GENERATING_SUMMARY') {
    statusLabel = 'Sintetizando resumo...';
  }

  return (
    <header class="chat-header">
      <div class="flex items-center gap-2 min-w-0 flex-1">
        <button
          onClick={() => activeContactSignal.value = null}
          class="md:hidden p-2 text-slate-300 hover:text-white rounded-xl bg-slate-800/80 border border-slate-700/80 font-bold text-xs flex items-center justify-center gap-1 shrink-0 h-9 w-9 cursor-pointer"
          title="Voltar para a lista de contatos"
        >
          <ArrowLeft class="w-4 h-4" />
        </button>
        <div class="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-slate-800 border border-slate-700/60 flex items-center justify-center font-bold text-white shadow-md shadow-cyan-500/10 text-sm shrink-0 overflow-hidden">
          {showPic ? (
            <img
              src={contact.profile_pic_url!}
              alt={contact.name}
              referrerPolicy="no-referrer"
              class="w-full h-full object-cover rounded-full"
              onError={() => setImgError(true)}
            />
          ) : isGroup ? (
            <Users class="w-4 h-4 sm:w-5 sm:h-5 text-purple-300" />
          ) : (
            <div class="w-full h-full bg-gradient-to-tr from-cyan-600 to-blue-600 flex items-center justify-center text-white font-bold">
              {initial}
            </div>
          )}
        </div>
        <div class="min-w-0 flex-1">
          <h2 class="text-xs sm:text-sm font-bold text-slate-50 truncate">{contact.name}</h2>
          <p class="text-[11px] text-slate-300 flex items-center gap-1.5 truncate">
            <span class="font-mono text-cyan-300 font-semibold truncate">
              {isGroup ? `Grupo` : contact.phone_number}
            </span>
            <span class="text-slate-500 hidden sm:inline">•</span>
            <span class="hidden sm:inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
              Monitoramento Ativo
            </span>
          </p>
        </div>
      </div>

      <div class="flex items-center gap-1.5 shrink-0">
        <button
          onClick={onOpenSummaryRangeModal}
          disabled={isJobActive}
          class={`relative overflow-hidden transition-[colors,width,box-shadow] duration-300 font-bold text-xs rounded-xl flex items-center justify-center cursor-pointer shadow-md h-9 ${
            isJobActive
              ? 'bg-slate-900 border border-amber-500/60 text-amber-300 px-2 sm:px-3 min-w-[110px] sm:min-w-[210px] cursor-not-allowed'
              : 'btn-header accent justify-center text-center px-2.5 sm:px-3 gap-1.5 h-9'
          }`}
          title={isJobActive ? `Sintetizando resumo: ${pct}% concluído` : 'Gerar síntese executiva'}
        >
          {isJobActive ? (
            <>
              <div
                class="absolute left-0 top-0 bottom-0 bg-gradient-to-r from-amber-600/30 via-cyan-600/40 to-indigo-600/50 transition-[width] duration-300 ease-out"
                style={{ width: `${pct}%` }}
              />
              <div class="relative z-10 flex items-center justify-between gap-1.5 w-full">
                <span class="flex items-center gap-1 shrink-0">
                  <Loader2 class="w-3.5 h-3.5 text-amber-300 animate-spin shrink-0" />
                  <span class="truncate max-w-[70px] sm:max-w-[145px] text-[10px] sm:text-[11px] font-semibold text-amber-200">
                    {statusLabel}
                  </span>
                </span>
                <span class="font-mono text-[10px] sm:text-[11px] font-bold text-amber-300 shrink-0 bg-slate-950/60 px-1 py-0.5 rounded border border-amber-500/30">
                  {pct}%
                </span>
              </div>
            </>
          ) : (
            <>
              <Sparkles class="w-3.5 h-3.5 text-amber-300 shrink-0" />
              <span class="hidden sm:inline">Gerar Resumo</span>
              <span class="sm:hidden text-[11px]">Resumo</span>
            </>
          )}
        </button>

        <button
          onClick={() => isAiChatDrawerOpenSignal.value = !isAiChatDrawerOpenSignal.value}
          class="btn-header primary justify-center text-center px-2.5 sm:px-3 gap-1.5 h-9"
        >
          <Bot class="w-3.5 h-3.5 text-cyan-300 shrink-0" />
          <span class="hidden sm:inline">Perguntar ao Alfred</span>
          <span class="sm:hidden text-[11px]">Alfred</span>
        </button>
      </div>
    </header>
  );
}
